
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { LogOut, User as UserIcon, Edit, Crown, Calendar, CreditCard, Clock, Copy, Check, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";
import { AnimatedAuthButton } from "@/components/AnimatedAuthButton";
import { EditProfileModal } from "@/components/auth/EditProfileModal";
import { usePremiumAccess } from "@/hooks/usePremiumAccess";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

export const UserAuthMenu = () => {
  const { user, signOut } = useAuth();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [subscriptionInfoOpen, setSubscriptionInfoOpen] = useState(false);
  const { hasAccess, purchaseDate, expiry, daysLeft } = usePremiumAccess();
  const [copiedUserId, setCopiedUserId] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (err) {
      // Optionally display a toast here
      console.error("Failed to logout", err);
    }
  };

  const handleEditSuccess = () => {
    // Refresh the page or update the user data
    window.location.reload();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  const copyUserId = () => {
    if (user?.id) {
      navigator.clipboard.writeText(user.id);
      setCopiedUserId(true);
      toast({
        title: "Copied!",
        description: "User ID copied to clipboard",
      });
      setTimeout(() => setCopiedUserId(false), 2000);
    }
  };


  // Get display name - prioritize full_name, fallback to email prefix
  const getDisplayName = () => {
    if (!user) return "";
    
    // If user has a full_name in user_metadata, use it
    if (user.user_metadata?.full_name) {
      return user.user_metadata.full_name;
    }
    
    // Otherwise, extract name from email (before @)
    if (user.email) {
      const emailPrefix = user.email.split("@")[0];
      // Capitalize first letter and replace dots/underscores with spaces
      return emailPrefix
        .replace(/[._]/g, " ")
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }
    
    return "User";
  };

  // Shorten email for display, e.g., jj...wat@gmail.com
  const getTruncatedEmail = (email: string) => {
    if (email.length <= 20) return email;
    const [name, domain] = email.split("@");
    if (name.length <= 6) return email;
    return `${name.slice(0, 2)}...${name.slice(-3)}@${domain}`;
  };

  return (
    <div className="flex items-center gap-2">
      {!user ? (
        <AnimatedAuthButton />
      ) : (
        <>
          {/* Desktop: Show display name, mobile: compact dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 group focus:outline-none rounded-lg px-3 py-2 hover:bg-gradient-to-r hover:from-purple-100 hover:to-pink-100 transition-all duration-300 transform hover:scale-105 bg-gradient-to-r from-white/80 to-purple-50/80 backdrop-blur-sm border border-purple-200/50 shadow-lg hover:shadow-xl max-w-[200px] lg:max-w-[250px]">
                <div className="relative">
                  <UserIcon className="w-5 h-5 text-purple-700 group-hover:text-purple-800 transition-colors duration-300 flex-shrink-0" />
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full animate-pulse"></div>
                </div>
                {/* Show display name on md+, hide on small screens */}
                <span
                  className="hidden md:inline-block text-purple-800 font-semibold text-sm truncate group-hover:text-purple-900 transition-colors duration-300"
                  title={getDisplayName()}
                >
                  {getDisplayName()}
                </span>
                <Crown className="w-4 h-4 text-yellow-500 animate-sparkle hidden lg:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="end" 
              className="w-80 max-w-[90vw] bg-gradient-to-br from-white via-purple-50 to-pink-50 border-2 border-purple-200/50 shadow-2xl backdrop-blur-lg animate-fade-in"
            >
              <DropdownMenuLabel className="break-all text-sm font-normal px-4 py-3 bg-gradient-to-r from-purple-100 to-pink-100 rounded-t-md">
                <div className="flex items-center gap-3 mb-3">
                  <div className="relative">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                      <UserIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full border-2 border-white animate-pulse"></div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-purple-800 text-lg truncate animate-fade-in">
                      {getDisplayName()}
                    </div>
                    {/* <div className="text-purple-600 text-xs flex items-center gap-1">
                      <Crown className="w-3 h-3 text-yellow-500" />
                      <span>Premium Member</span>
                    </div> */}
                  </div>
                </div>
                <div className="text-gray-600 text-xs break-all bg-white/50 rounded-md px-2 py-1 border border-purple-200/30">
                  📧 {user.email}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-gradient-to-r from-purple-200 to-pink-200" />
              
              <DropdownMenuItem asChild className="cursor-pointer">
                <Link to="/parent-dashboard" className="flex items-center gap-3 mx-2 my-1 px-3 py-2 rounded-md hover:bg-gradient-to-r hover:from-cyan-100 hover:to-blue-100 transition-all duration-300 group">
                  <div className="p-1 bg-gradient-to-r from-cyan-100 to-blue-100 rounded-md group-hover:scale-110 transition-transform duration-300">
                    <BarChart3 className="w-4 h-4 text-cyan-600" />
                  </div>
                  <span className="font-medium text-gray-700 group-hover:text-gray-900">Parent Dashboard</span>
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem
                className="cursor-pointer flex items-center gap-3 mx-2 my-1 px-3 py-2 rounded-md hover:bg-gradient-to-r hover:from-purple-100 hover:to-pink-100 transition-all duration-300 group"
                onClick={() => setEditModalOpen(true)}
              >
                <div className="p-1 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-md group-hover:scale-110 transition-transform duration-300">
                  <Edit className="w-4 h-4 text-blue-600" />
                </div>
                <span className="font-medium text-gray-700 group-hover:text-gray-900">Edit Profile</span>
              </DropdownMenuItem>

              {/* Subscription Info Section */}
              <div className="mx-2 my-1">
                <Collapsible 
                  open={subscriptionInfoOpen} 
                  onOpenChange={setSubscriptionInfoOpen}
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={`w-full ${hasAccess ? 'border-green-200 text-green-700 hover:bg-green-50' : 'border-gray-200 text-gray-700 hover:bg-gray-50'} justify-between px-3 py-2 h-auto`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="p-1 bg-gradient-to-r from-cyan-100 to-blue-100 rounded-md">
                          <CreditCard className="w-4 h-4 text-cyan-600" />
                        </div>
                        <span className="font-medium">Subscription Info</span>
                      </div>
                      <span className="text-xs">
                        {subscriptionInfoOpen ? '▲' : '▼'}
                      </span>
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <div className={`p-3 rounded-lg border ${hasAccess ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className="space-y-2 text-sm">
					  
					  
					   <div className="mt-2 flex items-center justify-between">
                  <span className="text-gray-600 text-xs">🆔 User ID:</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                      {user.id.substring(0, 8)}...
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={copyUserId}
                      className="h-6 w-6 p-0"
                    >
                      {copiedUserId ? (
                        <Check className="w-3 h-3 text-green-600" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                    </Button>
                  </div>
                </div>
					  
					  
                        {hasAccess && (
                          <>
                            <div className="flex items-center justify-between">
                              <span className="flex items-center gap-1 text-gray-600">
                                <Calendar className="w-3 h-3" />
                                Started:
                              </span>
                              <span className="font-medium text-green-700">
                                {purchaseDate ? formatDate(purchaseDate) : '-'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="flex items-center gap-1 text-gray-600">
                                <Clock className="w-3 h-3" />
                                Expires:
                              </span>
                              <span className="font-medium text-green-700">
                                {expiry ? formatDate(expiry) : '-'}
                              </span>
                            </div>
                            {daysLeft !== null && daysLeft > 0 && (
                              <div className="flex items-center justify-between pt-1 border-t border-gray-200">
                                <span className="text-gray-600">Days Left:</span>
                                <Badge 
                                  variant="outline" 
                                  className={`font-medium ${
                                    daysLeft > 30 
                                      ? 'border-green-500 text-green-700' 
                                      : daysLeft > 7
                                      ? 'border-yellow-500 text-yellow-700'
                                      : 'border-red-500 text-red-700'
                                  }`}
                                >
                                  {daysLeft} days
                                </Badge>
                              </div>
                            )}
                          </>
                        )}
                        <div className="pt-2 border-t border-gray-200">
                        {hasAccess ? (
                            <Badge className="w-full justify-center bg-green-100 text-green-800 hover:bg-green-200">
                              ✨ Premium Active ✨
                            </Badge>
                          ) : (
                            <Badge className="w-full justify-center bg-gray-100 text-gray-800 hover:bg-gray-200">
                              📦 No Active Subscription
                            </Badge>
                          )}

                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>

              <DropdownMenuItem
                className="cursor-pointer flex items-center gap-3 mx-2 my-1 px-3 py-2 rounded-md hover:bg-gradient-to-r hover:from-red-100 hover:to-pink-100 transition-all duration-300 group"
                onClick={handleLogout}
              >
                <div className="p-1 bg-gradient-to-r from-red-100 to-pink-100 rounded-md group-hover:scale-110 transition-transform duration-300">
                  <LogOut className="w-4 h-4 text-red-600" />
                </div>
                <span className="font-medium text-gray-700 group-hover:text-gray-900">Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <EditProfileModal
            isOpen={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            user={user}
            onSuccess={handleEditSuccess}
          />
        </>
      )}
    </div>
  );
};

export default UserAuthMenu;
